import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { AccountingService } from 'src/app/accounting/services/accounting.service';
import { ProductServiceItem } from 'src/app/shared/models/accounting';

/**
 * ag-grid cell editor that gives the user a type-ahead dropdown of matching
 * products/services. Returns the selected item's UPC (or name when no UPC)
 * so the existing onCellValueChanged handler can look it up and populate
 * the rest of the row.
 */
@Component({
  selector: 'app-product-service-autocomplete',
  templateUrl: './product-service-autocomplete.component.html',
  styleUrls: ['./product-service-autocomplete.component.scss'],
  standalone: true,
  imports: [],
})
export class ProductServiceAutocompleteComponent
  implements ICellEditorAngularComp, AfterViewInit, OnDestroy
{
  @ViewChild('input') input!: ElementRef<HTMLInputElement>;

  value: string = '';
  matches: ProductServiceItem[] = [];
  highlightIndex = 0;
  /** Dynamic max-height (in px) for the matches list. Sized at agInit based
   * on available viewport space above/below the cell so the popup can fit
   * correctly even when the cell is near the bottom of the screen. */
  matchesMaxHeight: number = 320;

  private params: any;
  private readonly MAX_RESULTS = 10;
  /** Hard ceiling — we never grow beyond this even if there's lots of space.
   * Matches the prior CSS rule. */
  private readonly MAX_MATCHES_HEIGHT_PX = 320;
  /** Floor — never shrink below this; if there's truly no room, the popup
   * will overflow but stays usable. */
  private readonly MIN_MATCHES_HEIGHT_PX = 120;
  /** Approx pixels consumed by the input row + container padding/border.
   * Used so available-space math reserves room for the chrome. */
  private readonly POPUP_CHROME_PX = 50;
  /** Margin we leave between popup edge and viewport edge so the popup
   * doesn't kiss the screen edge. */
  private readonly VIEWPORT_PADDING_PX = 20;
  private scrollHandler?: () => void;

  constructor(private accountingService: AccountingService) {}

  /** ag-grid asks for this to render the editor outside the cell so the
   * dropdown isn't clipped by the row. */
  isPopup(): boolean {
    return true;
  }

  agInit(params: any): void {
    this.params = params;
    // params.charPress is set when the user starts editing by typing a key.
    // Seed the input so that first keystroke isn't lost.
    this.value = (params.charPress ?? params.value ?? '').toString();
    this.updateMatches();
    // Compute the matches-list max-height NOW (before the popup is measured
    // and positioned by ag-grid) so the popup's full size is known up front.
    // If we waited until ngAfterViewInit, ag-grid would have already placed
    // the popup at the wrong coordinates assuming the larger 320px height.
    this.computeMatchesMaxHeight();
  }

  /** Size the matches list to fit in whichever side of the cell has more
   * room. Cell-near-bottom → use space above. Cell-near-top → use space below.
   * Result is clamped between MIN/MAX so the dropdown stays usable. */
  private computeMatchesMaxHeight(): void {
    const cellEl = this.params?.eGridCell as HTMLElement | undefined;
    if (!cellEl?.getBoundingClientRect) return;
    const rect = cellEl.getBoundingClientRect();
    const spaceBelow =
      window.innerHeight - rect.bottom - this.VIEWPORT_PADDING_PX - this.POPUP_CHROME_PX;
    const spaceAbove =
      rect.top - this.VIEWPORT_PADDING_PX - this.POPUP_CHROME_PX;
    const available = Math.max(spaceBelow, spaceAbove);
    this.matchesMaxHeight = Math.max(
      this.MIN_MATCHES_HEIGHT_PX,
      Math.min(this.MAX_MATCHES_HEIGHT_PX, available),
    );
  }

  ngAfterViewInit(): void {
    // Defer focus + reposition so the popup is in the DOM before we grab it.
    setTimeout(() => {
      const el = this.input?.nativeElement;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
      this.repositionPopup();
    });

    // Close the editor whenever any ancestor scrolls. We use position:fixed
    // for the popup, so scrolling moves the cell but not the popup —
    // closing it is the cleanest behavior and matches user intent.
    // capture:true is required because scroll events don't bubble.
    this.scrollHandler = () => this.params?.stopEditing();
    window.addEventListener('scroll', this.scrollHandler, {
      capture: true,
      passive: true,
    });
  }

  /** Override ag-grid's built-in popup placement with explicit fixed
   * coordinates derived from the cell's bounding rect. When flipping above
   * the cell we anchor the popup's BOTTOM (not top) so the popup naturally
   * grows/shrinks against the cell as the matches list filters down,
   * without leaving a gap between the popup and its row. */
  private repositionPopup(): void {
    const cellEl = this.params?.eGridCell as HTMLElement | undefined;
    const wrapper = this.findPopupWrapper();
    if (!cellEl?.getBoundingClientRect || !wrapper) return;

    const cellRect = cellEl.getBoundingClientRect();
    const popupHeight = wrapper.offsetHeight;
    const popupWidth = wrapper.offsetWidth;
    const PADDING = 8;

    // Vertical: prefer below the cell. Flip above if not enough room AND
    // there's more room above than below.
    const spaceBelow = window.innerHeight - cellRect.bottom - PADDING;
    const spaceAbove = cellRect.top - PADDING;
    const placeAbove = popupHeight > spaceBelow && spaceAbove > spaceBelow;

    // Horizontal: align with the cell's left edge. If the popup would
    // overflow the right edge of the viewport, slide it left to fit.
    let left = cellRect.left;
    if (left + popupWidth > window.innerWidth - PADDING) {
      left = window.innerWidth - popupWidth - PADDING;
    }
    if (left < PADDING) left = PADDING;

    wrapper.style.position = 'fixed';
    wrapper.style.left = `${left}px`;

    if (placeAbove) {
      // Bottom-anchoring keeps the popup glued to the cell as content shrinks.
      // (window.innerHeight - cellRect.top) is the distance from the viewport
      // bottom to the cell's top, which is what `bottom: Xpx; position: fixed`
      // wants.
      wrapper.style.bottom = `${window.innerHeight - cellRect.top}px`;
      wrapper.style.top = 'auto';
    } else {
      wrapper.style.top = `${cellRect.bottom}px`;
      wrapper.style.bottom = 'auto';
    }
  }

  /** Walk up from our input element to find ag-grid's popup wrapper, which
   * is the element with the inline positioning we need to override. */
  private findPopupWrapper(): HTMLElement | null {
    let el: HTMLElement | null = this.input?.nativeElement?.parentElement || null;
    while (el && el !== document.body) {
      if (
        el.classList?.contains('ag-popup-child') ||
        el.classList?.contains('ag-custom-component-popup')
      ) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  ngOnDestroy(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler, { capture: true });
      this.scrollHandler = undefined;
    }
  }

  getValue(): string {
    return this.value;
  }

  onInput(e: Event): void {
    this.value = (e.target as HTMLInputElement).value;
    this.highlightIndex = 0;
    this.updateMatches();
  }

  onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown') {
      if (!this.matches.length) return;
      e.preventDefault();
      this.highlightIndex = (this.highlightIndex + 1) % this.matches.length;
    } else if (e.key === 'ArrowUp') {
      if (!this.matches.length) return;
      e.preventDefault();
      this.highlightIndex =
        (this.highlightIndex - 1 + this.matches.length) % this.matches.length;
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      // When a suggestion is highlighted, commit that. Otherwise let
      // ag-grid's default handler commit whatever the user typed.
      if (this.matches.length && this.highlightIndex >= 0) {
        e.preventDefault();
        this.selectMatch(this.matches[this.highlightIndex]);
      }
    } else if (e.key === 'Escape') {
      this.params.stopEditing(true);
    }
  }

  selectMatch(item: ProductServiceItem): void {
    this.value = item.upc || item.name || '';
    this.params.stopEditing();
  }

  /** Close the editor (committing what was typed) when the user clicks
   * anywhere outside it. The dropdown items use mousedown+preventDefault to
   * keep focus, so clicking a suggestion doesn't trigger blur. */
  onBlur(): void {
    this.params?.stopEditing();
  }

  private updateMatches(): void {
    const q = this.value.trim().toLowerCase();
    const items = (this.accountingService.products_services || []).filter(
      (i: any) => i?.active !== false && !i?.archived,
    );
    if (!q) {
      this.matches = items.slice(0, this.MAX_RESULTS);
      return;
    }
    this.matches = items
      .filter(
        (i: any) =>
          (i.name && String(i.name).toLowerCase().includes(q)) ||
          (i.upc && String(i.upc).toLowerCase().includes(q)) ||
          (i.description && String(i.description).toLowerCase().includes(q)) ||
          (i.category && String(i.category).toLowerCase().includes(q)),
      )
      .slice(0, this.MAX_RESULTS);
  }
}
